/**
 * #1386: Org Detail's Kalshi/Polymarket panel reads. A non-2xx, network-failed
 * or unparseable read is unavailable, never an empty list; a valid empty array
 * stays authoritative and clears the panel. The #435 Trader Detail shape
 * (`unwrapManagerPolyFills` / `nextPolyFillsPanel`), kept beside this view.
 */
import { readPositionsSnapshot } from '@/stores/accountStore';
import type { OwnerFilterKey } from '@/lib/ownerAttribution';
import type { KalshiPosition } from '@/types/kalshi';

export { nextOrgPanel, orgPanelTileCount, unavailableOrgPanel, unreadOrgPanel } from './orgReadPanel';
export type { OrgReadPanel } from './orgReadPanel';

/**
 * The org fills list, or null when the read cannot be trusted: a non-2xx, a
 * body that did not parse (`null`), no `fills` array, or an element that is
 * not an object. Only the shape is checked, never field values. Not
 * `parseFillsSnapshot`: that is the Kalshi fill contract, and this tape mixes
 * Kalshi and Polymarket rows. Orders use `parseOrdersSnapshot` (#238).
 */
export function decodeOrgFillRows(ok: boolean, payload: unknown): Array<Record<string, unknown>> | null {
  if (!ok || !payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const rows = (payload as Record<string, unknown>).fills;
  if (!Array.isArray(rows)) return null;
  if (!rows.every((r) => r !== null && typeof r === 'object' && !Array.isArray(r))) return null;
  return rows as Array<Record<string, unknown>>;
}

/**
 * #1394: what the org account read says about the venue's holdings — the
 * source of the unattributed (residual) and sibling-instance rows.
 * 'pending' while the read is out; 'unknown' when it cannot be trusted.
 */
export type OrgAccountHoldings = 'pending' | 'known' | 'unknown';

/**
 * The org's Kalshi venue positions from `/api/manager/org/account`, or null
 * when they are unknown: a non-2xx, a body that did not parse, the relay's
 * `errors.positions: true` (its Kalshi positions call failed and it answered
 * 200 with `positions: []`, manager.ts `/org/account`), or a list the strict
 * snapshot reader rejects (missing, not an array, or any malformed row — a
 * partly dropped account is indistinguishable from a complete one). A valid
 * empty list is a confirmed empty account.
 */
export function decodeOrgAccountPositions(ok: boolean, payload: unknown): KalshiPosition[] | null {
  if (!ok || !payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
  const body = payload as { positions?: unknown; errors?: { positions?: unknown } | null };
  if (body.errors?.positions === true) return null;
  // Only `positions`: the reader's other containers must not pick up another field.
  const read = readPositionsSnapshot({ positions: body.positions });
  return read.ok ? read.positions : null;
}

/**
 * Whether the account snapshot leaves the positions shown under this owner
 * filter complete. The snapshot carries only unattributed and sibling-instance
 * rows, so a local trader's or bot's view never depends on it (the same
 * owner-scoped rule as `usOrgOwnerComplete`); every other view does.
 */
export const orgAccountOwnerComplete = (holdings: OrgAccountHoldings, owner: OwnerFilterKey): boolean =>
  holdings === 'known' || owner.startsWith('trader:') || owner.startsWith('bot:');
